import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronUp, ChevronLeft, ChevronRight, X, Images } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { ourPeopleContent as c } from "../mock";

const journeyPhotos = [
  { video: "/journey-clip.mp4", caption: "A Moment in Kashmir" },
  { src: "/journey-4.webp", caption: "Monte-Carlo Beach" },
  { src: "/journey-3.webp", caption: "On the Bosphorus · Istanbul" },
  { src: "/journey-1.webp", caption: "Monte Carlo Beach Club" },
  { src: "/journey-2.webp", caption: "Celebrations in the Kitchen" },
  { src: "/journey-6.webp", caption: "Among the Pines · Kashmir" },
  { src: "/journey-5.webp", caption: "The Whole House · Kashmir" },
  { src: "/journey-7.webp", caption: "The Builders of the House" },
];

const OurPeople = () => {
  const [showAppreciations, setShowAppreciations] = useState(false);
  const [showJourney, setShowJourney] = useState(false);
  const [slide, setSlide] = useState(0);
  const touchStartX = useRef(null);

  const navSlide = useCallback((dir) => {
    setSlide((s) => (s + dir + journeyPhotos.length) % journeyPhotos.length);
  }, []);

  const openJourney = () => {
    setSlide(0);
    setShowJourney(true);
  };

  useEffect(() => {
    if (!showJourney) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowJourney(false);
      else if (e.key === "ArrowRight") navSlide(1);
      else if (e.key === "ArrowLeft") navSlide(-1);
    };
    window.addEventListener("keydown", onKey);
    const timer = setInterval(() => navSlide(1), 5000);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearInterval(timer);
    };
  }, [showJourney, navSlide]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) navSlide(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-ivory">
      <Header />

      {/* Founder letter */}
      <section className="pt-40 pb-narrative-gap px-margin-mobile md:px-margin-desktop">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-gutter md:gap-16 items-start">
          <Reveal className="col-span-1 md:col-span-5">
            <div className="w-full aspect-[4/5] overflow-hidden bg-brand-ink">
              <img
                src={c.founder.portrait}
                alt={c.founder.name}
                className="w-full h-full object-cover object-top"
                decoding="async"
              />
            </div>
          </Reveal>
          <div className="col-span-1 md:col-span-6 md:col-start-7">
            <Reveal
              as="h1"
              className="font-cormorant text-display-lg-mobile md:text-[45px] leading-[1.05] text-brand-ink mb-10"
            >
              {c.founder.title}
            </Reveal>
            {c.founder.letter.map((p, i) => (
              <Reveal
                as="p"
                key={p}
                delay={i * 90}
                className="font-cormorant text-body-lg text-deep-taupe mb-6"
              >
                {p}
              </Reveal>
            ))}
            <Reveal delay={420} className="mt-10">
              <p className="font-inter text-label-sm uppercase tracking-widest text-brand-ink mb-1">
                {c.founder.name}
              </p>
              <p className="font-inter text-eyebrow uppercase tracking-widest text-brand-secondary">
                {c.founder.role}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* People and Practice */}
      <section className="bg-brand-ink px-margin-mobile md:px-margin-desktop py-narrative-gap">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal as="p" className="font-inter text-eyebrow uppercase tracking-widest text-bright-gold-on-dark mb-8">
            {c.people.eyebrow}
          </Reveal>
          <Reveal
            as="blockquote"
            delay={100}
            className="font-cormorant text-[23px] md:text-[33px] leading-snug text-ink-on-dark italic mb-8"
          >
            &ldquo;{c.people.quote}&rdquo;
          </Reveal>
          <Reveal as="p" delay={200} className="font-cormorant text-body-lg text-ink-on-dark/70">
            {c.people.body}
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-gutter mt-16 max-w-6xl mx-auto">
          {c.people.roles.map((r, i) => (
            <Reveal
              key={r.title}
              delay={i * 100}
              className="border-t border-ink-on-dark/20 pt-6"
            >
              <h3 className="font-cormorant text-body-lg text-ink-on-dark mb-3">
                {r.title}
              </h3>
              <p className="font-cormorant text-body-md text-ink-on-dark/60">
                {r.detail}
              </p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={200} className="mt-16 text-center">
          <button
            type="button"
            onClick={openJourney}
            className="inline-flex items-center gap-3 border border-bright-gold-on-dark/60 px-8 py-4 text-ink-on-dark hover:bg-ink-on-dark hover:text-brand-ink hover:border-ink-on-dark transition-all duration-500 group/journey"
            data-testid="view-journey-button"
          >
            <Images className="w-4 h-4 transition-transform duration-500 group-hover/journey:scale-110" />
            <span className="font-inter text-label-sm uppercase tracking-widest">
              Moments From Our Journey
            </span>
          </button>
        </Reveal>
      </section>

      {/* Honours */}
      <section className="px-margin-mobile md:px-margin-desktop py-narrative-gap">
        <div className="max-w-5xl mx-auto">
          <Reveal as="p" className="font-inter text-eyebrow uppercase tracking-widest text-brand-secondary mb-12 text-center">
            {c.honours.eyebrow}
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2">
            {c.honours.items.map((h, i) => (
              <Reveal
                key={h.title}
                delay={i * 80}
                className="border-t border-hairline-beige py-8"
              >
                <p className="font-inter text-[10px] tracking-[0.3em] uppercase text-brand-secondary mb-3">
                  {h.source}
                </p>
                <h3 className="font-cormorant text-headline-md text-brand-ink mb-2">
                  {h.title}
                </h3>
                <p className="font-cormorant text-body-md text-deep-taupe">
                  {h.detail}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Wall of Appreciation */}
      <section className="bg-cover-ivory px-margin-mobile md:px-margin-desktop py-narrative-gap">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal as="p" className="font-inter text-eyebrow uppercase tracking-widest text-brand-secondary mb-6">
            {c.wall.eyebrow}
          </Reveal>
          <Reveal
            as="h2"
            delay={100}
            className="font-cormorant text-display-lg-mobile md:text-[39px] text-brand-ink mb-6"
          >
            {c.wall.heading}
          </Reveal>
          <Reveal as="p" delay={200} className="font-cormorant text-body-md text-deep-taupe mb-14">
            {c.wall.body}
          </Reveal>
          <Reveal
            as="blockquote"
            delay={280}
            className="font-cormorant text-[18px] md:text-[23px] leading-relaxed text-brand-ink italic mb-8"
          >
            &ldquo;{c.wall.quote}&rdquo;
          </Reveal>
          <Reveal as="p" delay={340} className="font-inter text-eyebrow uppercase tracking-widest text-brand-secondary mb-10">
            {c.wall.attribution}
          </Reveal>
          {!showAppreciations && (
            <Reveal delay={380}>
              <button
                onClick={() => setShowAppreciations(true)}
                data-testid="view-appreciations-button"
                className="inline-flex items-center gap-3 border border-brand-gold px-8 py-4 text-brand-ink hover:bg-brand-ink hover:text-ink-on-dark hover:border-brand-ink transition-all duration-500"
              >
                <span className="font-inter text-label-sm uppercase tracking-widest">
                  View Accolades &amp; Appreciations
                </span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </Reveal>
          )}
        </div>

        {/* Grid of appreciations */}
        {showAppreciations && (
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-gutter mb-16 mt-16" data-testid="appreciations-grid">
          {c.wall.testimonials.map((t, i) => (
            <Reveal
              key={t.quote}
              delay={(i % 3) * 100}
              className="bg-background-ivory border border-hairline-beige p-8 flex flex-col"
            >
              <span className="font-cormorant text-brand-gold text-[40px] leading-none mb-2">&ldquo;</span>
              <p className="font-cormorant text-body-md text-brand-ink italic mb-6 flex-grow">
                {t.quote}
              </p>
              <div className="border-t border-hairline-beige pt-4">
                {t.name && (
                  <p className="font-inter text-label-sm uppercase tracking-widest text-brand-ink">
                    {t.name}
                  </p>
                )}
                <p className="font-inter text-eyebrow uppercase tracking-widest text-brand-secondary mt-1">
                  {t.caption}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        )}

        {showAppreciations && (
          <div className="text-center mb-16" data-testid="hide-appreciations-wrap">
            <button
              onClick={() => setShowAppreciations(false)}
              data-testid="hide-appreciations-button"
              className="inline-flex items-center gap-3 border border-hairline-beige px-8 py-4 text-deep-taupe hover:border-brand-ink hover:text-brand-ink transition-all duration-500"
            >
              <span className="font-inter text-label-sm uppercase tracking-widest">
                Hide Appreciations
              </span>
              <ChevronUp className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="max-w-3xl mx-auto text-center">
          <Reveal delay={400}>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 border border-brand-gold px-8 py-4 text-brand-ink hover:bg-brand-ink hover:text-ink-on-dark hover:border-brand-ink transition-all duration-500"
            >
              <span className="font-inter text-label-sm uppercase tracking-widest">
                Begin a Conversation
              </span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {showJourney && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-sm flex items-center justify-center p-6 animate-fade-in"
          onClick={() => setShowJourney(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          data-testid="journey-slideshow"
        >
          <button
            onClick={() => setShowJourney(false)}
            className="absolute top-6 right-6 text-ink-on-dark/70 hover:text-ink-on-dark transition-colors z-20"
            data-testid="journey-close"
            aria-label="Close"
          >
            <X className="w-7 h-7" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navSlide(-1);
            }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-ink-on-dark/40 text-ink-on-dark/70 hover:text-ink-on-dark hover:border-ink-on-dark/80 flex items-center justify-center backdrop-blur-sm transition-colors z-20"
            data-testid="journey-prev"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navSlide(1);
            }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-ink-on-dark/40 text-ink-on-dark/70 hover:text-ink-on-dark hover:border-ink-on-dark/80 flex items-center justify-center backdrop-blur-sm transition-colors z-20"
            data-testid="journey-next"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <figure
            className="flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {journeyPhotos[slide].video ? (
              <video
                key={journeyPhotos[slide].video}
                className="max-w-[92vw] max-h-[78vh] object-contain shadow-2xl bg-black animate-fade-in"
                autoPlay
                muted
                loop
                playsInline
                data-testid="journey-video"
              >
                <source src={journeyPhotos[slide].video} type="video/mp4" />
              </video>
            ) : (
              <img
                key={journeyPhotos[slide].src}
                src={journeyPhotos[slide].src}
                alt={journeyPhotos[slide].caption}
                className="max-w-[92vw] max-h-[78vh] object-contain shadow-2xl animate-fade-in"
                loading="lazy"
                decoding="async"
              />
            )}
            <figcaption className="font-inter text-eyebrow uppercase tracking-widest text-ink-on-dark/70 mt-5">
              {journeyPhotos[slide].caption}
            </figcaption>
            <div className="flex items-center gap-2 mt-5">
              {journeyPhotos.map((p, di) => (
                <button
                  key={p.caption}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSlide(di);
                  }}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    di === slide
                      ? "w-8 bg-bright-gold-on-dark"
                      : "w-1.5 bg-ink-on-dark/40 hover:bg-ink-on-dark/70"
                  }`}
                  data-testid={`journey-dot-${di}`}
                  aria-label={`Go to slide ${di + 1}`}
                />
              ))}
            </div>
          </figure>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default OurPeople;
